import React, { Ref, useState } from 'react';
import { find, propEq } from 'ramda';

import { Artwork, ArtworkVariant } from '@definitions/artworks.types';
import { COLORS } from '@theme/colors';
import { renderWhenTrue } from '@utils/rendering';
import { Sidebar, SidebarHeadline } from '@components/PDP/pdp.styles';
import { KLARNA_CONTAINER_OFFSET } from '@components/klarnaPlacement';
import { SpaceViewStep } from '@hooks/useArtworkSpaceView/useArtworkSpaceView';
import { useSellingState } from '@hooks/useArtworks/useArtworks';

import { ArtworkSidebarLinks } from '../artworkSidebarLinks';
import { DescriptionBlock } from '../descriptionBlock';
import { ArtworkDetails } from '../artworkDetails';
import { ArtworkSpaceViewDetails } from '../artworkSpaceViewDetails';

const SPACE_VIEW_BUTTON_HEIGHT = 174;

export interface ArtworkSidebarProps {
  artwork: Artwork;
  defaultVariant: ArtworkVariant | null;
  setBackgroundColor: (color: COLORS) => void;
  isSpaceViewMode: boolean;
  toggleSpaceViewMode: () => void;
  openSpaceViewMode: () => void;
  setBackgroundImage: (value: string) => void;
  backgroundImage: string;
  measure: number;
  distance: number;
  setMeasure: (value: number) => void;
  spaceViewStep: SpaceViewStep;
  setSpaceViewStep: (value: number) => void;
  downloadImage: () => void;
  innerRef?: Ref<HTMLDivElement>;
  imagesInView: boolean;
  isInStock: boolean;
}

export const ArtworkSidebar = ({
  artwork,
  setBackgroundColor,
  defaultVariant,
  isSpaceViewMode,
  toggleSpaceViewMode,
  openSpaceViewMode,
  backgroundImage,
  setBackgroundImage,
  measure,
  setMeasure,
  spaceViewStep,
  setSpaceViewStep,
  downloadImage,
  distance,
  imagesInView,
  isInStock,
}: ArtworkSidebarProps) => {
  const thumbnail = find(propEq('isThumbnail', true), artwork.images) || artwork.primaryImage;
  const isBcCdn = !artwork.urlStandard;
  const { isPostSellingDate, isWithinSellingDates } = useSellingState(artwork);
  const [descriptionHeight, setDescriptionHeight] = useState(0);

  const renderArtworkSidebarLinks = renderWhenTrue(() => (
    <ArtworkSidebarLinks images={artwork.images} setBackgroundColor={setBackgroundColor} />
  ));

  const renderArtworkSpaceViewDetails = renderWhenTrue(() => (
    <ArtworkSpaceViewDetails
      isSpaceViewMode={isSpaceViewMode}
      toggleSpaceViewMode={toggleSpaceViewMode}
      setBackgroundImage={setBackgroundImage}
      backgroundImage={backgroundImage}
      measure={measure}
      setMeasure={setMeasure}
      spaceViewStep={spaceViewStep}
      setSpaceViewStep={setSpaceViewStep}
      downloadImage={downloadImage}
      distance={distance}
      openSpaceViewMode={openSpaceViewMode}
      descriptionHeight={descriptionHeight}
    />
  ));

  return (
    <Sidebar>
      <SidebarHeadline>
        <ArtworkDetails artwork={artwork} defaultVariant={defaultVariant} />
        <div style={{ height: descriptionHeight + SPACE_VIEW_BUTTON_HEIGHT + KLARNA_CONTAINER_OFFSET }}>
          <DescriptionBlock
            category={artwork.category}
            subCategory={artwork.subcategory}
            description={artwork.description}
            image={thumbnail!}
            title={artwork.title}
            artist={artwork.artist}
            liveEndAt={artwork.liveEndAt}
            setDescriptionHeight={setDescriptionHeight}
            imagesInView={imagesInView}
            isInStock={isInStock}
            price={artwork.price}
            isPostSellingDate={isPostSellingDate}
            isWithinSellingDates={isWithinSellingDates}
            inventoryLevel={artwork.inventoryLevel}
            lowInventoryLevel={artwork.lowInventoryLevel}
          />
          {artwork.category.toLowerCase() != 'products' && renderArtworkSpaceViewDetails(!isBcCdn)}
        </div>
      </SidebarHeadline>
      {renderArtworkSidebarLinks(!isSpaceViewMode)}
    </Sidebar>
  );
};
